import { Button, InlineBanner, Text } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { useState } from 'react'
import {
  CurrencyDetails,
  PromotionModal,
  PromotionModalOptions,
  TabTypes,
} from '@vend/business-components'
import {
  useCustomerManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { getPromotionById } from '../../promotions/promotions-resource/promotions-resource'
import { selectPotentialPromotionId } from '../../../state/slices/currentSale/currentSaleSlice'

interface Props {
  shouldShowPromoBanner: boolean
}

export const SalePromotionBanner: React.FC<Props> = ({
  shouldShowPromoBanner,
}) => {
  const t = useT()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const potentialPromotionId = useAppSelector(selectPotentialPromotionId)
  const [
    modalOptions,
    setModalOptions,
  ] = useState<PromotionModalOptions | null>(null)

  if (!potentialPromotionId) {
    return null
  }

  const openPromotionModal = async () => {
    const promotion = await getPromotionById(potentialPromotionId)
    if (!promotion) {
      return
    }

    const retailer = retailerSettingsManager.get()
    const currencyDetails: CurrencyDetails = {
      culture: retailer.culture,
      code: retailer.currency.code,
      symbol: retailer.currency.symbol,
    }
    const lineItemIds = saleManager.getLineItemProductIds()
    const isTaxExclusive = !registerManager.isTaxInclusive()
    const customerGroups = await customerManager.getCustomerGroups()

    setModalOptions({
      promotion,
      lineItemIds,
      isTaxExclusive,
      totalCustomerGroups: customerGroups ? customerGroups.length : 0,
      defaultTab: TabTypes.Buy,
      currencyDetails,
    })
  }

  return (
    <>
      {modalOptions && (
        <PromotionModal
          close={() => setModalOptions(null)}
          options={modalOptions}
        />
      )}

      {shouldShowPromoBanner && (
        <InlineBanner
          variant="info"
          icon="ticket"
          data-cy="promotion-helper-banner"
        >
          <Text type="label">
            {t('A promotion is available on this sale.', {
              _context: 'Sale promotion banner',
            })}
            &nbsp;
            <Button
              link
              className="vd-link vd-link--secondary"
              onClick={openPromotionModal}
              data-testid="sale-promotion-banner-button"
              data-track="show-promotion-details"
            >
              {t('Show details', {
                _context: 'Sale promotion banner modal link',
              })}
            </Button>
          </Text>
        </InlineBanner>
      )}
    </>
  )
}
